import {
  getConfigTenant,
  isCorpTenant,
  useGetUserMetadataQuery,
  useIsTravelApp,
} from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import dayjs from "dayjs";
import {
  ActionLink,
  BannerSeverity,
  CloseButtonIcon,
  Header,
  Icon,
  IconName,
  MobileMegaMenu,
  MobileMegaMenuData,
  NotificationBanner,
} from "halifax";
import React, { useEffect } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  IdEnum,
  IIdLodgings,
  IResult,
  NonEmptyArray,
  RecentPackageSearch,
} from "redmond";
import { fetchFlightLocation } from "../../../../api/v0/search/fetchFlightLocation";
import { fetchHotelLocation } from "../../../../api/v0/search/fetchHotelLocation";
import {
  ACTION_BASED_DISCOVERY,
  getExperimentVariantCustomVariants,
  LOCATION_BASED_DISCOVERY,
  PACKAGES_DISCOVERY_DRIVEN_SEARCH,
  PACKAGES_DISCOVERY_DRIVEN_SEARCH_VARIANTS,
  useExperiments,
} from "../../../../context/experiments";
import { PATH_HOME, PATH_HOTELS_AVAILABILITY } from "../../../../utils/paths";
import { transformToStringifiedAvailabilityQuery } from "../../../availability/utils/queryStringHelpers";
import { CONFIRM_TRAVELER_DETAILS } from "../../textConstants";
import {
  IPackagesParsedQuery,
  parseQueryString,
} from "../../utils/parseQueryString";
import { HowPackagesWork } from "../HowPackagesWork";
import { LocationSearch, MobileCalendarPicker } from "./components";
import { MobilePackagesSearchControlConnectorProps } from "./container";
import "./styles.scss";
import {
  MOBILE_SEARCH_CONTROL_CALENDAR_TITLE,
  MOBILE_SEARCH_CONTROL_MEGA_MENU_TITLE,
  MOBILE_SEARCH_CONTROL_TITLE,
  MOBILE_SEARCH_CONTROL_TITLE_CORPORATE,
} from "./textConstants";
import { ACTION_BASED_MEGA_MENU_ROOT_NODES } from "../../actionBasedMegaMenuRootNodes";
import { LOCATION_BASED_MEGA_MENU_ROOT_NODES } from "../../locationBasedMegaMenuRootNodes";
import { trackEvent } from "../../../../api/v0/trackEvent";

enum MobilePackagesSearchStepType {
  CalendarPicker,
  Complete,
  LocationSearch,
  MegaMenu,
}
const LocationSearchStep = {
  type: MobilePackagesSearchStepType.LocationSearch,
} as const;
const CalendarPickerStep = {
  type: MobilePackagesSearchStepType.CalendarPicker,
} as const;
type MegaMenuStep = {
  type: MobilePackagesSearchStepType.MegaMenu;
  data?: MobileMegaMenuData;
};
const megaMenuStep = (data?: MobileMegaMenuData): MegaMenuStep => ({
  type: MobilePackagesSearchStepType.MegaMenu,
  data: data,
});
type MobilePackagesSearchStep =
  | typeof LocationSearchStep
  | typeof CalendarPickerStep
  | MegaMenuStep;

export interface IMobilePackagesSearchControlProps
  extends MobilePackagesSearchControlConnectorProps,
    RouteComponentProps {}

export const MobilePackagesSearchControl = ({
  history,
  departureDate,
  returnDate,
  destination,
  origin,
  travelers,
  stopsOptions,
  fareClassOptionFilter,
  setDepartureDate,
  setReturnDate,
  setOrigin,
  setDestination,
  setStopsOption,
  setFareclassOptionFilter,
  setTravelers,
}: IMobilePackagesSearchControlProps) => {
  const [navigationStack, setNavigationStack] = React.useState<
    NonEmptyArray<MobilePackagesSearchStep>
  >([LocationSearchStep]);
  const currentStep = React.useMemo(
    () => navigationStack.slice(-1)[0],
    [navigationStack],
  );
  const pushStep = React.useCallback((step: MobilePackagesSearchStep) => {
    setNavigationStack((prev) => [...prev, step]);
  }, []);
  const handleGoBack = () => {
    if (navigationStack.length > 1) {
      setNavigationStack(
        navigationStack.slice(0, -1) as NonEmptyArray<MobilePackagesSearchStep>,
      );
    }
  };

  const {
    data: userPreferenceMetadata,
    isLoading: userPreferencesMetadataLoading,
  } = useGetUserMetadataQuery({
    keys: ["hasSeenPackagesFtux"],
  });

  const expState = useExperiments();
  const packagesDiscoveryDrivenSearchVariant =
    getExperimentVariantCustomVariants(
      expState.experiments,
      PACKAGES_DISCOVERY_DRIVEN_SEARCH,
      PACKAGES_DISCOVERY_DRIVEN_SEARCH_VARIANTS,
    );

  const [recentSearches, setRecentSearches] = React.useState<
    RecentPackageSearch[]
  >([]);

  const handleSearchClick = () => {
    if (
      !!destination &&
      !!origin &&
      !!departureDate &&
      !!returnDate &&
      !!travelers.adultsCount
    ) {
      history.push(
        `${PATH_HOTELS_AVAILABILITY}${transformToStringifiedAvailabilityQuery(
          origin?.id?.code?.code,
          (destination?.id as IIdLodgings).lodgingSelection.searchTerm,
          (destination?.id as IIdLodgings).lodgingSelection.placeId,
          departureDate,
          returnDate,
          travelers.adultsCount,
          travelers.children,
          travelers.infants,
          stopsOptions,
          fareClassOptionFilter,
        )}`,
      );
    }
  };

  const focusedMonthIndex = departureDate
    ? dayjs(departureDate).diff(dayjs(), "month")
    : 0;

  const parsedQueryString = parseQueryString(history);
  const { isTravelApp } = useIsTravelApp();

  const populateFieldsFromQueryString = async ({
    fromDate,
    untilDate,
    origin,
    destination,
    stopsOption,
    fareClass,
    adultsCount,
    childrenCount,
    infantsInSeatCount,
    infantsOnLapCount,
  }: IPackagesParsedQuery) => {
    if (fromDate) {
      setDepartureDate(fromDate);
    }

    if (untilDate) {
      setReturnDate(untilDate);
    }

    if (origin) {
      const originLocation = await fetchFlightLocation(origin);
      setOrigin(originLocation);
    }

    if (destination) {
      const destinationLocation = await fetchHotelLocation(destination);
      setDestination(destinationLocation);
    }

    if (stopsOption) {
      setStopsOption(stopsOption);
    }

    if (fareClass) {
      setFareclassOptionFilter({
        basic: false,
        standard: false,
        enhanced: false,
        premium: false,
        luxury: false,
        [fareClass]: true,
      });
    }
    if (
      adultsCount ||
      childrenCount ||
      infantsInSeatCount ||
      infantsOnLapCount
    ) {
      // hardcoding to be age of 1 for infants and 11 for children per the ticket [CMKT-4767]
      const infantsArr = [
        ...(infantsInSeatCount
          ? new Array(infantsInSeatCount ?? 0).fill({ age: 1, inSeat: true })
          : []),
        ...(infantsOnLapCount
          ? new Array(infantsOnLapCount ?? 0).fill({ age: 1, inSeat: false })
          : []),
      ];
      setTravelers({
        adultsCount,
        children: childrenCount ? new Array(childrenCount).fill(11) : [],
        infants: infantsArr,
      });
    }
  };

  useEffect(() => {
    if (parsedQueryString) {
      (async () => {
        await populateFieldsFromQueryString(parsedQueryString);
      })();
    }
  }, []);

  useEffect(() => {
    if (isTravelApp) {
      pushStep(CalendarPickerStep);
    }
  }, [isTravelApp]);

  useEffect(() => {
    const getRecentSearches = () => {
      try {
        const localStorageItem = localStorage.getItem(
          "recently_searched_packages",
        );

        if (localStorageItem) {
          const parsedSearches: RecentPackageSearch[] =
            JSON.parse(localStorageItem);
          const sortedSearches = parsedSearches
            .filter(
              (search) =>
                !!search.originLocation && !!search.destinationLocation,
            )
            .sort(
              (a, b) =>
                new Date(b.searchDate).getTime() -
                new Date(a.searchDate).getTime(),
            );
          setRecentSearches(sortedSearches);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`Failed to get recent searches from local storage: ${e}`);
      }
    };
    getRecentSearches();
    // eslint-disable-next-line no-restricted-globals
    addEventListener("update_recently_searched_packages", getRecentSearches);
    return () =>
      // eslint-disable-next-line no-restricted-globals
      removeEventListener(
        "update_recently_searched_packages",
        getRecentSearches,
      );
  }, []);

  const isCorporate = isCorpTenant(getConfigTenant());

  const renderHeaderCenter = () => {
    let body;
    if (currentStep.type === MobilePackagesSearchStepType.CalendarPicker) {
      body = (
        <Box className="calendar-header">
          <Typography variant="body1">
            {MOBILE_SEARCH_CONTROL_CALENDAR_TITLE}
          </Typography>
        </Box>
      );
    } else {
      let title;
      if (currentStep.type == MobilePackagesSearchStepType.MegaMenu) {
        title = currentStep.data
          ? currentStep.data.data.label
          : MOBILE_SEARCH_CONTROL_MEGA_MENU_TITLE;
      } else if (isCorporate) {
        title = MOBILE_SEARCH_CONTROL_TITLE_CORPORATE;
      } else {
        title = MOBILE_SEARCH_CONTROL_TITLE;
      }
      body = (
        <Box className="mobile-packages-search-location-label">
          <Typography variant="body1">{title}</Typography>
        </Box>
      );
    }
    return <Box className="header-center-section">{body}</Box>;
  };

  const renderMegaMenu = () => {
    let rootNodes = undefined;
    if (packagesDiscoveryDrivenSearchVariant == LOCATION_BASED_DISCOVERY) {
      rootNodes = LOCATION_BASED_MEGA_MENU_ROOT_NODES;
    } else if (packagesDiscoveryDrivenSearchVariant == ACTION_BASED_DISCOVERY) {
      rootNodes = ACTION_BASED_MEGA_MENU_ROOT_NODES;
    }
    return (
      currentStep.type === MobilePackagesSearchStepType.MegaMenu && (
        <MobileMegaMenu
          rootNodes={rootNodes}
          data={currentStep.data}
          navigate={(data) => pushStep(megaMenuStep(data))}
          select={(item) => {
            const result: IResult = {
              id: {
                Id: IdEnum.Lodgings,
                lodgingSelection: item.lodgingSelection,
              },
              label: item.label,
            };
            setDestination(result);
            pushStep(LocationSearchStep);
          }}
          trackEvent={trackEvent}
        />
      )
    );
  };

  return (
    <Box className="mobile-packages-search">
      {!isTravelApp && (
        <Header
          className="homepage"
          center={renderHeaderCenter()}
          left={
            <ActionLink
              className={clsx("mobile-packages-search-header-go-back", {
                hidden:
                  currentStep.type ===
                  MobilePackagesSearchStepType.LocationSearch,
              })}
              onClick={handleGoBack}
              content={<Icon name={IconName.RightChevronIcon} />}
            />
          }
          right={
            <ActionLink
              className="mobile-packages-search-header-close"
              onClick={() => {
                history.push(PATH_HOME);
              }}
              content={<CloseButtonIcon />}
              label="Close"
            />
          }
          isMobile
          fullWidth
        />
      )}
      {currentStep.type === MobilePackagesSearchStepType.LocationSearch && (
        <>
          <LocationSearch
            recentSearches={recentSearches}
            onOpenMegaMenu={() => {
              pushStep(megaMenuStep());
            }}
            onComplete={() => {
              pushStep(CalendarPickerStep);
            }}
            history={history}
          />

          {(parsedQueryString?.childrenCount ||
            parsedQueryString?.infantsInSeatCount ||
            parsedQueryString?.infantsOnLapCount) &&
          parsedQueryString.entryPoint ? (
            <NotificationBanner
              className="confirm-travelers-banner"
              label={CONFIRM_TRAVELER_DETAILS}
              severity={BannerSeverity.WARNING}
            />
          ) : undefined}

          {!userPreferencesMetadataLoading &&
          !userPreferenceMetadata?.userMetadata?.hasSeenPackagesFtux ? (
            <HowPackagesWork className="mobile-packages-search-how-packages-work" />
          ) : undefined}
        </>
      )}
      {renderMegaMenu()}
      {currentStep.type === MobilePackagesSearchStepType.CalendarPicker && (
        <MobileCalendarPicker
          focusedMonthIndex={focusedMonthIndex}
          returnDate={returnDate}
          departure={departureDate}
          onComplete={handleSearchClick}
        />
      )}
    </Box>
  );
};
